// Castintech dashboard — Saglik (Health) view modulu.
// Faz 9b PR6 — inline'da 11 saglik+audit fonksiyonu (~305 satir) tek modulden geciyor:
// - Saglik: loadHealth, renderHealth, startSaglikTimer, stopSaglikTimer
// - Audit feed: loadAudit, loadMoreAudit, renderAudit, getAuditColor,
//               setAuditFilter, setAuditActorFilter, setAuditActionFilter
//
// State erisimi: state.js direct import (state.healthData, state.auditRows, vs.)
// Utils + config: utils.js (escHtml/escAttr/ago) + config.js (SAGLIK_REFRESH_MS).
// Api: api.js (getSystemHealth/getAccounts/getAuditRecent).
//
// Inline call-site'lar bridge.js araciligi ile window.X erisir:
// - HTML inline change/click: setAuditFilter, setAuditActorFilter, setAuditActionFilter, loadMoreAudit (Faz 10 PR6'da delegated)
// - dashboard render dispatcher: renderHealth, renderAudit
// - SSE handler (sse.js zaten window.renderAudit kullaniyor)

import { state } from '../state.js';
import * as Api from '../api.js';
import { escHtml, escAttr, ago, $, asEl } from '../utils.js';
import { SAGLIK_REFRESH_MS } from '../config.js';

interface ServiceRow {
  label: string;
  ok: boolean;
  warn?: boolean;
  val: string;
}

// ── Health (servis durumu + accounts + bot) ──────────────────────────

export async function loadHealth(): Promise<void> {
  const sub = $('saglik-sub');
  if (sub) sub.textContent = 'sistem durumu yükleniyor...';
  try {
    const [hRes, aRes] = await Promise.all([
      Api.getSystemHealth(),
      Api.getAccounts(),
    ]);
    if (!hRes.ok) throw new Error('health HTTP ' + hRes.status);
    state.healthData = await hRes.json();
    state.healthAccounts = aRes.ok ? await aRes.json() : null;
    renderHealth();
  } catch (e) {
    console.warn('loadHealth failed:', (e as Error).message);
    if (sub) sub.textContent = 'hata: ' + (e as Error).message;
  }
}

export function renderHealth(): void {
  const sub = $('saglik-sub');
  const sBody = $('hc-services-body');
  const fBody = $('hc-fallback-body');
  const aBody = $('hc-accounts-body');
  const bBody = $('hc-bot-body');
  if (!state.healthData) {
    if (sBody) sBody.textContent = '—';
    if (fBody) fBody.textContent = '—';
    if (aBody) aBody.textContent = '—';
    if (bBody) bBody.textContent = '—';
    return;
  }

  // Services card
  const svcs: ServiceRow[] = [];
  const pp = state.healthData.paperclip || ({} as { ok?: boolean; status?: number; error?: string });
  svcs.push({ label: 'Paperclip', ok: !!pp.ok, val: pp.ok ? `OK (${pp.status || 200})` : (pp.error || 'down') });
  const mr = state.healthData.model_router || ({} as { ok?: boolean; error?: string });
  svcs.push({ label: 'Model Router', ok: !mr.error && mr.ok !== false, val: mr.error ? mr.error.substring(0, 60) : 'OK' });
  svcs.push({ label: 'Notify', ok: true, val: 'OK (yanıt veriyor)' });
  // Faz 8a — Postgres direct probe (SELECT 1) + latency
  const pg = state.healthData.postgres || ({} as { ok?: boolean; latency_ms?: number; error?: string });
  if (pg.ok !== undefined || pg.error) {
    const pgWarn = !!pg.ok && (pg.latency_ms || 0) > 200;
    svcs.push({
      label: 'Postgres',
      ok: !!pg.ok,
      warn: pgWarn,
      val: pg.ok ? `OK (${pg.latency_ms ?? '?'} ms)` : (pg.error ? String(pg.error).substring(0, 60) : 'down'),
    });
  }
  const df = state.healthData.data_files || {};
  const filesArr: Array<{ name?: string; mtime?: string }> = Array.isArray(df)
    ? df
    : Object.entries(df).map(([k, v]) => ({ name: k, ...(v as { mtime?: string }) }));
  filesArr.forEach(f => {
    const mtimeMs = f.mtime ? new Date(f.mtime).getTime() : 0;
    const ageDays = mtimeMs ? (Date.now() - mtimeMs) / 86400000 : 999;
    const fOk = ageDays <= 7;
    svcs.push({ label: f.name || 'data', ok: fOk, val: mtimeMs && f.mtime ? `${ago(f.mtime)} önce` : 'yok', warn: ageDays > 7 && ageDays <= 30 });
  });
  if (sBody) {
    sBody.innerHTML = svcs.map(s => {
      const cls = s.ok ? 'ok' : (s.warn ? 'warn' : 'err');
      return `<div class="hc-row"><span class="hc-dot ${cls}"></span><b>${escHtml(s.label)}</b><span class="hc-val mono">${escHtml(String(s.val))}</span></div>`;
    }).join('');
  }

  // Fallback 24h card
  const fb = state.healthData.fallback_24h || ({} as { primary_ok?: number; fallback_ok?: number; fail?: number });
  const fbPrimary = fb.primary_ok || 0;
  const fbFallback = fb.fallback_ok || 0;
  const fbFail = fb.fail || 0;
  const total = fbPrimary + fbFallback + fbFail;
  const failRatio = total ? fbFail / total : 0;
  const fbStatus = failRatio >= 0.2 ? 'err' : (failRatio >= 0.05 ? 'warn' : 'ok');
  const pPct = total ? (fbPrimary / total) * 100 : 0;
  const fPct = total ? (fbFallback / total) * 100 : 0;
  const xPct = total ? (fbFail / total) * 100 : 0;
  if (fBody) {
    if (!total) {
      fBody.innerHTML = `<div class="hc-row"><span class="hc-dot ok"></span><span class="hc-val">Son 24 saatte kayıt yok</span></div>`;
    } else {
      fBody.innerHTML = `
        <div class="hc-row"><b>Toplam:</b><span class="hc-val mono">${total} call</span><span class="hc-pill ${fbStatus}">${fbStatus==='ok'?'OK':(fbStatus==='warn'?'UYARI':'HATA')}</span></div>
        <div class="hc-bar">
          <div class="hc-bar-seg primary" style="width:${pPct.toFixed(1)}%"></div>
          <div class="hc-bar-seg fallback" style="width:${fPct.toFixed(1)}%"></div>
          <div class="hc-bar-seg fail" style="width:${xPct.toFixed(1)}%"></div>
        </div>
        <div class="hc-row"><span class="hc-dot ok"></span><b>Primary:</b><span class="hc-val mono">${fbPrimary} (${pPct.toFixed(0)}%)</span></div>
        <div class="hc-row"><span class="hc-dot warn"></span><b>Fallback:</b><span class="hc-val mono">${fbFallback} (${fPct.toFixed(0)}%)</span></div>
        <div class="hc-row"><span class="hc-dot err"></span><b>Fail:</b><span class="hc-val mono">${fbFail} (${xPct.toFixed(0)}%)</span></div>`;
    }
  }

  // Accounts card
  if (aBody) {
    const list = (state.healthAccounts && Array.isArray(state.healthAccounts.accounts)) ? state.healthAccounts.accounts : [];
    if (!list.length) {
      aBody.innerHTML = `<div class="hc-row"><span class="hc-dot warn"></span><span class="hc-val">Hesap bulunamadı</span></div>`;
    } else {
      const rows = list.map((a: { name?: string; id?: string; expiresAt?: string }) => {
        const exp = a.expiresAt ? new Date(a.expiresAt) : null;
        const days = exp ? Math.ceil((exp.getTime() - Date.now()) / 86400000) : null;
        const cls = days === null ? 'warn' : (days < 0 ? 'err' : (days < 10 ? 'warn' : 'ok'));
        const dStr = days === null ? '—' : (days < 0 ? `${-days}g geçti` : `${days}g`);
        return `<tr><td><span class="hc-dot ${cls}" style="display:inline-block;margin-right:6px;vertical-align:middle"></span>${escHtml(a.name || a.id || '?')}</td><td class="hc-val mono">${dStr}</td></tr>`;
      }).join('');
      aBody.innerHTML = `<table class="hc-table"><thead><tr><th>Hesap</th><th>Kalan</th></tr></thead><tbody>${rows}</tbody></table>`;
    }
  }

  // Telegram bot card
  let botOk = false, botWarn = false;
  if (bBody) {
    const tg = state.healthData.telegram_bot || ({} as NonNullable<typeof state.healthData.telegram_bot>);
    if (tg.skipped) {
      botWarn = true;
      bBody.innerHTML = `<div class="hc-row"><span class="hc-dot warn"></span><span class="hc-val">${escHtml(tg.error || 'token yok')}</span></div>`;
    } else {
      const tokenCls = tg.ok ? 'ok' : 'err';
      const tokenVal = tg.ok ? `@${escHtml(tg.username || 'bot')}` : escHtml(String(tg.error || 'invalid').substring(0, 60));
      const lastAt = tg.last_admin_msg_at;
      const lastAgeH = lastAt ? (Date.now() - new Date(lastAt).getTime()) / 3600000 : null;
      const lastCls = lastAgeH === null ? 'warn' : (lastAgeH > 24 ? 'warn' : 'ok');
      const lastVal = lastAt ? `${ago(lastAt)} önce` : 'kayıt yok';
      let html = `<div class="hc-row"><span class="hc-dot ${tokenCls}"></span><b>Token:</b><span class="hc-val mono">${tokenVal}</span></div>`;
      html += `<div class="hc-row"><span class="hc-dot ${lastCls}"></span><b>Son admin msg:</b><span class="hc-val mono">${lastVal}</span></div>`;
      if (tg.webhook) {
        const wh = tg.webhook;
        const whCls = wh.last_error_date ? 'warn' : (wh.pending > 5 ? 'warn' : 'ok');
        const whTxt = `pending ${wh.pending}${wh.last_error_message ? ' · err' : ''}`;
        html += `<div class="hc-row"><span class="hc-dot ${whCls}"></span><b>Webhook:</b><span class="hc-val mono">${escHtml(whTxt)}</span></div>`;
      }
      bBody.innerHTML = html;
      botOk = !!tg.ok && (lastCls === 'ok');
      botWarn = !botOk && !!tg.ok;
    }
    svcs.push({ label: 'Telegram Bot', ok: botOk, warn: botWarn, val: botOk ? 'OK' : (botWarn ? 'uyarı' : 'hata') });
  }

  // Status sub
  if (sub) {
    const okCount = svcs.filter(s => s.ok).length;
    const total2 = svcs.length;
    sub.textContent = `${okCount}/${total2} servis OK · fallback ${fbStatus.toUpperCase()} · ${ago(new Date().toISOString())} güncellendi`;
  }
}

// ── Audit feed (loadAudit / loadMoreAudit / renderAudit + setters) ──

export async function loadAudit({ append = false }: { append?: boolean } = {}): Promise<void> {
  try {
    const qs = new URLSearchParams({ limit: '50' });
    if (state.auditFilter) qs.set('title', state.auditFilter);
    if (state.auditActorFilter) qs.set('actor', state.auditActorFilter);
    if (state.auditActionFilter) qs.set('action', state.auditActionFilter);
    // Faz 8b — keyset cursor (ikisi birden ya da hiç).
    if (append && state.auditCursor && state.auditCursor.before_ts && state.auditCursor.before_id != null) {
      qs.set('before_ts', state.auditCursor.before_ts);
      qs.set('before_id', String(state.auditCursor.before_id));
    }
    const r = await Api.getAuditRecent(qs.toString());
    if (!r.ok) throw new Error('audit HTTP ' + r.status);
    const j = await r.json();
    const newRows = Array.isArray(j.rows) ? j.rows : [];
    if (append) {
      state.auditRows = state.auditRows.concat(newRows);
    } else {
      state.auditRows = newRows;
      // Fresh fetch'te facet dropdownlari yenile; append'te koru.
      state.auditTitles = Array.isArray(j.available_titles) ? j.available_titles : [];
      state.auditActors = Array.isArray(j.available_actors) ? j.available_actors : [];
      state.auditActions = Array.isArray(j.available_actions) ? j.available_actions : [];
    }
    state.auditCursor = j.next_cursor || null;
    state.auditHasMore = !!j.has_more;
    renderAudit();
  } catch (e) {
    const msg = (e as Error).message;
    console.warn('loadAudit failed:', msg);
    if (!append) {
      const feed = $('audit-feed');
      if (feed) feed.innerHTML = `<div class="audit-empty">Audit feed yüklenemedi: ${escHtml(msg)}</div>`;
    }
  }
}

export async function loadMoreAudit(): Promise<void> {
  if (state.auditLoading || !state.auditHasMore || !state.auditCursor) return;
  state.auditLoading = true;
  // Render hemen — buton disabled görünür.
  renderAudit();
  try { await loadAudit({ append: true }); }
  finally { state.auditLoading = false; renderAudit(); }
}

export function setAuditFilter(v: string): void {
  state.auditFilter = v || '';
  // Cursor reset (filter degisince fresh fetch)
  state.auditCursor = null;
  state.auditHasMore = false;
  loadAudit();
}

export function setAuditActorFilter(v: string): void {
  state.auditActorFilter = v || '';
  state.auditCursor = null;
  state.auditHasMore = false;
  loadAudit();
}

export function setAuditActionFilter(v: string): void {
  state.auditActionFilter = v || '';
  state.auditCursor = null;
  state.auditHasMore = false;
  loadAudit();
}

export function getAuditColor(title: string | undefined): string {
  const t = String(title || '').toLowerCase();
  if (t === 'telegram-admin') return 'indigo';
  if (t === 'castagent sistemi') return 'garnet';
  if (t.startsWith('vps health')) return 'topaz';
  if (t === 'info') return 'muted';
  // Faz 18 PR-A — specialized SSE event badges. Mevcut palette'i kullanir
  // (yeni CSS class eklemeden); long_running -> topaz (uyari rengi),
  // lifecycle -> indigo (musteri yasam dongusu), invoice -> garnet (finans).
  if (t === 'long-running-start' || t === 'long-running-status') return 'topaz';
  if (t === 'lifecycle-transition') return 'indigo';
  if (t === 'invoice-created' || t === 'invoice-status-change') return 'garnet';
  return 'muted';
}

// Faz 18 PR-A — title-bazli emoji prefix (specialized event tipini hizli
// gostermek icin). audit row badge'inin yanina bir family icon koyar;
// generic audit row'lar icin null doner (mevcut goruntu degismez).
export function getAuditFamilyIcon(title: string | undefined): string | null {
  const t = String(title || '').toLowerCase();
  if (t === 'long-running-start' || t === 'long-running-status') return '⏱️';
  if (t === 'lifecycle-transition') return '🔄';
  if (t === 'invoice-created' || t === 'invoice-status-change') return '🧾';
  return null;
}

export function renderAudit(): void {
  const feed = $('audit-feed');
  const filter = $<HTMLSelectElement>('audit-title-filter');
  const actorFilter = $<HTMLSelectElement>('audit-actor-filter');
  const actionFilter = $<HTMLSelectElement>('audit-action-filter');
  const meta = $('audit-meta');
  if (!feed) return;

  // Update filter dropdowns
  if (filter) {
    const opts = ['<option value="">Başlık (tümü)</option>']
      .concat((state.auditTitles || []).map(t => `<option value="${escAttr(t)}">${escHtml(t)}</option>`));
    filter.innerHTML = opts.join('');
    filter.value = state.auditFilter || '';
  }
  if (actorFilter) {
    const opts = ['<option value="">Aktör (tümü)</option>']
      .concat((state.auditActors || []).map(a => `<option value="${escAttr(a)}">${escHtml(a)}</option>`));
    actorFilter.innerHTML = opts.join('');
    actorFilter.value = state.auditActorFilter || '';
  }
  if (actionFilter) {
    const opts = ['<option value="">Aksiyon (tümü)</option>']
      .concat((state.auditActions || []).map(a => `<option value="${escAttr(a)}">${escHtml(a)}</option>`));
    actionFilter.innerHTML = opts.join('');
    actionFilter.value = state.auditActionFilter || '';
  }

  if (meta) meta.textContent = `${state.auditRows.length} kayıt`;

  if (!state.auditRows.length) {
    const fParts = [];
    if (state.auditFilter) fParts.push('başlık=' + state.auditFilter);
    if (state.auditActorFilter) fParts.push('aktör=' + state.auditActorFilter);
    if (state.auditActionFilter) fParts.push('aksiyon=' + state.auditActionFilter);
    feed.innerHTML = `<div class="audit-empty">📭 Audit kaydı yok${fParts.length ? ' (filtre: ' + escHtml(fParts.join(', ')) + ')' : ''}</div>`;
    return;
  }

  const rowsHtml = state.auditRows.map(r => {
    const color = getAuditColor(r.title);
    const familyIcon = getAuditFamilyIcon(r.title);
    const titleLabel = familyIcon ? `${familyIcon} ${r.title || '?'}` : (r.title || '?');
    const tags: string[] = [];
    if (r.actor) tags.push(`<span class="audit-tag" title="aktör">👤 ${escHtml(r.actor)}</span>`);
    if (r.action) tags.push(`<span class="audit-tag" title="aksiyon">⚡ ${escHtml(r.action)}</span>`);
    if (r.resource_type) {
      const rid = r.resource_id ? ':' + String(r.resource_id).substring(0, 24) : '';
      tags.push(`<span class="audit-tag" title="kaynak">📎 ${escHtml(r.resource_type)}${escHtml(rid)}</span>`);
    }
    let metaHtml = '';
    if (r.metadata && typeof r.metadata === 'object') {
      const pairs = Object.entries(r.metadata).slice(0, 4).map(([k, v]) => `${escHtml(k)}=${escHtml(String(v).substring(0, 40))}`);
      if (pairs.length) metaHtml = `<div class="audit-meta-line">${escHtml(pairs.join(' · '))}</div>`;
    }
    return `
      <div class="audit-row">
        <span class="audit-badge ${color}">${escHtml(titleLabel)}</span>
        <div class="audit-body">
          <div class="audit-msg">${escHtml(String(r.message || '').substring(0, 600))}</div>
          ${tags.length ? `<div class="audit-tags">${tags.join('')}</div>` : ''}
          ${metaHtml}
        </div>
        <span class="audit-time" title="${escAttr(r.created_at || '')}">${ago(r.created_at || '')} önce</span>
      </div>`;
  }).join('');

  // Faz 8b — "Daha eski kayıtları yükle" butonu (yalnız has_more=true ise).
  const loadMoreHtml = state.auditHasMore
    ? `<button class="audit-load-more" id="audit-load-more" data-action="load-more-audit" ${state.auditLoading ? 'disabled' : ''}>${state.auditLoading ? 'Yükleniyor…' : 'Daha eski kayıtları yükle'}</button>`
    : '';
  feed.innerHTML = rowsHtml + loadMoreHtml;
}

// ── Saglik refresh timer ─────────────────────────────────────────────

export function startSaglikTimer(): void {
  stopSaglikTimer();
  state.saglikTimer = setInterval(() => {
    if (state.currentPage === 'saglik') {
      loadHealth();
      // SSE bagliyken audit event-driven; fallback timer onu zaten polling'e cevirir.
      if (!state.sseConn) loadAudit();
    }
  }, SAGLIK_REFRESH_MS);
}

export function stopSaglikTimer(): void {
  if (state.saglikTimer) {
    clearInterval(state.saglikTimer as ReturnType<typeof setInterval>);
    state.saglikTimer = null;
  }
}

// Faz 10 PR6 — Saglik view static + audit feed delegation.
// Statik: refresh button + 3 audit filter (title/actor/action change).
// Dynamic: #audit-feed → data-action=load-more-audit.
let _saglikStaticBound = false;
export function bindSaglikStatic(): void {
  if (_saglikStaticBound) return;
  // Saglik refresh button (loadHealth + loadAudit sequence)
  const refresh = $('saglik-refresh-btn');
  if (refresh) refresh.addEventListener('click', () => { loadHealth(); loadAudit(); });
  // 3 audit filter (change event, currentTarget value)
  const titleSel = $<HTMLSelectElement>('audit-title-filter');
  if (titleSel) titleSel.addEventListener('change', (e) => setAuditFilter((e.currentTarget as HTMLSelectElement).value));
  const actorSel = $<HTMLSelectElement>('audit-actor-filter');
  if (actorSel) actorSel.addEventListener('change', (e) => setAuditActorFilter((e.currentTarget as HTMLSelectElement).value));
  const actionSel = $<HTMLSelectElement>('audit-action-filter');
  if (actionSel) actionSel.addEventListener('change', (e) => setAuditActionFilter((e.currentTarget as HTMLSelectElement).value));
  _saglikStaticBound = true;
}

// Audit feed load-more button delegation (renderAudit her seferinde innerHTML rebuild eder).
let _auditFeedDelegated = false;
export function bindAuditFeedDelegation(): void {
  if (_auditFeedDelegated) return;
  const feed = $('audit-feed');
  if (!feed) return;
  feed.addEventListener('click', (e) => {
    const btn = asEl(e.target)?.closest<HTMLElement>('[data-action="load-more-audit"]');
    if (!btn || !feed.contains(btn)) return;
    loadMoreAudit();
  });
  _auditFeedDelegated = true;
}
